import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { notificationsAPI } from "../../lib/api";

const SURFACE = "#0A0D13";
const SURFACE_2 = "#0D1117";
const BORDER = "#1E2433";
const TEXT_PRIMARY = "#F9FAFB";
const TEXT_MUTED = "#6B7280";
const TEXT_DIM = "#374151";
const ACCENT = "#2563EB";

const TYPE_COLOR = {
  material_change: "#EF4444",
  minor_change: "#F59E0B",
  fit_changed: "#A855F7",
  scan_completed: "#6B7280",
  new_articles: "#2563EB",
  team_activity: "#2563EB",
  analysis_ready: "#22C55E",
  analysis_failed: "#EF4444",
};

const timeAgo = iso => {
  if (!iso) return "";
  const diff = Math.max(0, Date.now() - new Date(iso).getTime());
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

/**
 * Live notification bell — monitoring deltas and analysis lifecycle events,
 * written server-side as they happen and gated by the user's notification
 * preferences. Polls every 60s while mounted.
 */
export const NotificationBell = ({ onViewAnalysis, onNavigate }) => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const ref = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await notificationsAPI.list({ limit: 20 });
      setItems(res.data?.notifications || []);
      setUnread(res.data?.unread_count || 0);
    } catch (e) { /* bell stays quiet if the feed is unavailable */ }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, [load]);

  useEffect(() => {
    if (!open) return;
    const onDown = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const handleClickItem = async (n) => {
    if (!n.read) {
      try {
        const res = await notificationsAPI.markRead(n.notification_id);
        setUnread(res.data?.unread_count ?? Math.max(0, unread - 1));
        setItems(prev => prev.map(x => x.notification_id === n.notification_id ? { ...x, read: true } : x));
      } catch (e) { /* keep the UI responsive */ }
    }
    setOpen(false);
    const link = n.link || {};
    if (link.kind === "analysis" && link.analysis_id && onViewAnalysis) onViewAnalysis(link.analysis_id);
    else if (link.kind === "monitor" && onNavigate) onNavigate("monitor");
  };

  const handleMarkAll = async () => {
    try {
      await notificationsAPI.markAllRead();
      setItems(prev => prev.map(x => ({ ...x, read: true })));
      setUnread(0);
      toast.success("All notifications marked read");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not mark notifications read");
    }
  };

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        data-testid="notification-bell-btn"
        onClick={() => setOpen(o => !o)}
        aria-label="Notifications"
        style={{
          width: 34, height: 34, borderRadius: 8,
          background: open ? "#161B27" : SURFACE_2,
          border: `1px solid ${open ? "#374151" : BORDER}`,
          display: "flex", alignItems: "center", justifyContent: "center",
          cursor: "pointer", position: "relative", transition: "all 0.15s",
        }}>
        <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke={TEXT_MUTED} strokeWidth={1.8} strokeLinecap="round">
          <path d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
        {unread > 0 && (
          <span data-testid="notification-unread-badge" style={{
            position: "absolute", top: -4, right: -4, minWidth: 16, height: 16,
            borderRadius: 8, background: "#EF4444", color: "#fff",
            fontSize: 9, fontWeight: 700, fontFamily: "'DM Mono', monospace",
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: "0 4px", border: `1.5px solid ${SURFACE}`,
          }}>{unread > 9 ? "9+" : unread}</span>
        )}
      </button>

      {open && (
        <div data-testid="notification-dropdown" style={{
          position: "absolute", top: 42, right: 0, width: 340, zIndex: 100,
          background: SURFACE_2, border: `1px solid ${BORDER}`, borderRadius: 10,
          boxShadow: "0 20px 40px rgba(0,0,0,0.5)", overflow: "hidden",
        }}>
          <div style={{
            padding: "12px 16px", borderBottom: `1px solid ${BORDER}`,
            display: "flex", justifyContent: "space-between", alignItems: "center",
          }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: TEXT_PRIMARY, fontFamily: "'DM Sans', sans-serif" }}>
              Notifications
            </span>
            {unread > 0 && (
              <button data-testid="notification-mark-all-btn" onClick={handleMarkAll} style={{
                background: "none", border: "none", fontSize: 11, color: ACCENT,
                cursor: "pointer", fontFamily: "'DM Sans', sans-serif", padding: 0,
              }}>Mark all read</button>
            )}
          </div>

          <div style={{ maxHeight: 360, overflowY: "auto" }}>
            {loading ? (
              <div style={{ padding: "20px 16px", textAlign: "center", color: TEXT_DIM, fontSize: 12 }}>Loading…</div>
            ) : items.length === 0 ? (
              <div data-testid="notifications-empty" style={{ padding: "20px 16px", textAlign: "center", color: TEXT_DIM, fontSize: 12, lineHeight: 1.6 }}>
                No notifications yet.<br />
                <span style={{ fontSize: 11 }}>Monitoring changes and finished briefs land here.</span>
              </div>
            ) : items.map(n => (
              <button
                key={n.notification_id}
                data-testid={`notification-item-${n.notification_id}`}
                onClick={() => handleClickItem(n)}
                style={{
                  width: "100%", textAlign: "left", background: n.read ? "transparent" : `${ACCENT}0A`,
                  border: "none", borderBottom: `1px solid ${BORDER}`,
                  padding: "12px 16px", cursor: "pointer", display: "flex", gap: 10,
                  fontFamily: "'DM Sans', sans-serif",
                }}
                onMouseEnter={e => e.currentTarget.style.background = "#0D1117"}
                onMouseLeave={e => e.currentTarget.style.background = n.read ? "transparent" : `${ACCENT}0A`}
              >
                <span style={{
                  width: 6, height: 6, borderRadius: "50%", flexShrink: 0, marginTop: 6,
                  background: TYPE_COLOR[n.type] || TEXT_MUTED,
                  opacity: n.read ? 0.4 : 1,
                }} />
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{
                    display: "block", fontSize: 12.5, color: n.read ? TEXT_MUTED : TEXT_PRIMARY,
                    fontWeight: n.read ? 400 : 600, marginBottom: 2, lineHeight: 1.45,
                  }}>{n.title}</span>
                  {n.body && (
                    <span style={{ display: "block", fontSize: 11.5, color: TEXT_MUTED, lineHeight: 1.5, marginBottom: 3 }}>
                      {n.body}
                    </span>
                  )}
                  <span style={{ fontSize: 10, color: TEXT_DIM, fontFamily: "'DM Mono', monospace" }}>
                    {timeAgo(n.created_at)}
                  </span>
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
